"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAccount as useWagmiAccount,
  useChainId,
  useConfig,
  useSendTransaction,
  useWriteContract,
  useSwitchChain,
} from "wagmi";
import { useProfileBalance, useClient } from "@getpara/react-sdk";
import { getWalletClient as getWalletClientAction, switchChain as switchChainAction } from "@wagmi/core";
import { createPublicClient, encodeFunctionData, http, isAddress } from "viem";
import * as viemChains from "viem/chains";
import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddress,
  createTransferInstruction,
  getAccount,
  createAssociatedTokenAccountInstruction,
  getMint,
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
} from "@solana/spl-token";
import { parseUnits, formatEther } from "viem";
import {
  Send,
  ChevronDown,
  Search,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Wallet,
} from "lucide-react";
import { useParaTransactions } from "@/hooks/use-para-transactions";
import { getChainById } from "@/constants/get-chain";
import {
  SendModalProps,
  TransactionStatus,
  TokenBalance,
} from "@/constants/types";
import { getChainIdForToken } from "@/constants/get-chain";
import { useTokenBalances } from "@/hooks/use-token-balances";
import { useParaAccount } from "@/hooks/use-appkit-account";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { readAndStoreOverride } from "@/lib/on-chain-balance-reader";
import { isNativeToken } from "@/constants/send-modal-helpers";
import { logDiagnostic } from "@/lib/silent-diagnostic";
import { pushHistoryEntry } from "@/lib/transaction-history";
import { getExplorerUrl as getExplorerUrlByChain, getSolanaExplorerUrl } from "@/constants/explorer-urls";

const SOLANA_GAS_RESERVE = 0.001; // Reserve for transaction fees

const CHAIN_RPCS: Record<number, string> = {
  1: "/api/rpc?chain=ethereum",
  56: "https://bsc-dataseed1.binance.org",
  137: "https://polygon-rpc.com",
  42161: "https://arb1.arbitrum.io/rpc",
  10: "https://mainnet.optimism.io",
  43114: "https://api.avax.network/ext/bc/C/rpc",
  8453: "https://mainnet.base.org",
  59144: "https://rpc.linea.build",
  534352: "https://rpc.scroll.io",
  81457: "https://rpc.blast.io",
};

const VIEM_CHAINS: Record<number, viemChains.Chain> = {
  1: viemChains.mainnet,
  56: viemChains.bsc,
  137: viemChains.polygon,
  42161: viemChains.arbitrum,
  10: viemChains.optimism,
  43114: viemChains.avalanche,
  8453: viemChains.base,
};

// Estimate gas cost for a native EVM transfer using live gas price
const estimateEvmGasCost = async (tokenChainId: number): Promise<number> => {
  try {
    const chain = getChainById(tokenChainId);
    if (!chain) return 0.001;

    const client = createPublicClient({ chain, transport: http() });
    const gasPrice = await client.getGasPrice();
    // Native transfer = 21000 gas, add 50% buffer for price fluctuations
    const gasCost = (BigInt(21000) * gasPrice * BigInt(150)) / BigInt(100);
    return parseFloat(formatEther(gasCost));
  } catch {
    return 0.001;
  }
};

const getEthereumMainnetGasParams = async () => {
  const chain = VIEM_CHAINS[1];
  const rpcUrl = CHAIN_RPCS[1];

  if (!chain || !rpcUrl) {
    return {};
  }

  const client = createPublicClient({
    chain,
    transport: http(rpcUrl),
  });

  try {
    const feeData = await client.estimateFeesPerGas();
    if (feeData.maxFeePerGas && feeData.maxPriorityFeePerGas) {
      return {
        maxFeePerGas: feeData.maxFeePerGas,
        maxPriorityFeePerGas: feeData.maxPriorityFeePerGas,
      };
    }
  } catch (error: any) {
    logDiagnostic({ event: "transfer-gas-params-eip1559-error", message: error?.message?.slice(0, 200) });
    console.warn("Failed to fetch EIP-1559 gas params, falling back to gasPrice", error);
  }

  try {
    const gasPrice = await client.getGasPrice();
    return { gasPrice };
  } catch (error: any) {
    logDiagnostic({ event: "transfer-gas-params-legacy-error", message: error?.message?.slice(0, 200) });
    console.warn("Failed to fetch legacy gasPrice", error);
    return {};
  }
};

const ERC20_ABI = [
  {
    name: "transfer",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
] as const;


const parseErc20TransferAmount = ({
  amount,
  decimals,
  tokenSymbol,
}: {
  amount: string;
  decimals: number | undefined;
  tokenSymbol: string;
}): bigint => {
  if (!Number.isInteger(decimals) || (decimals ?? -1) < 0) {
    throw new Error(
      `Invalid token decimals for ${tokenSymbol}. Expected a non-negative integer, received: ${String(decimals)}`
    );
  }

  const normalizedDecimals = decimals as number;

  try {
    return parseUnits(amount, normalizedDecimals);
  } catch (error) {
    console.error("[SendModal] Failed to parse ERC-20 amount", {
      tokenSymbol,
      amount,
      decimals,
      error,
    });
    throw new Error(
      `Invalid transfer amount for ${tokenSymbol}. Could not convert amount using ${normalizedDecimals} decimals.`
    );
  }
};

const assertValidErc20TransferParams = ({
  tokenAddress,
  recipient,
  amount,
  tokenSymbol,
}: {
  tokenAddress: string | undefined;
  recipient: string;
  amount: bigint;
  tokenSymbol: string;
}) => {
  if (!tokenAddress || !isAddress(tokenAddress)) {
    throw new Error(
      `Invalid token contract address for ${tokenSymbol}. Received: ${tokenAddress ?? "undefined"}`
    );
  }

  if (!recipient || !isAddress(recipient)) {
    throw new Error(`Invalid recipient address for ${tokenSymbol}: ${recipient || "undefined"}`);
  }

  if (amount <= BigInt(0)) {
    throw new Error(`Transfer amount for ${tokenSymbol} must be greater than 0.`);
  }
};

const safeFormatAmount = (value: string): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return "0";
  // Use 4 decimal places max, truncated not rounded
  const parts = num.toFixed(8).split(".");
  return `${parts[0]}.${parts[1].slice(0, 4)}`;
};

export function SendModal({
  isOpen,
  onClose,
  token: initialToken,
  network: initialNetwork,
}: SendModalProps) {
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [txStatus, setTxStatus] = useState<TransactionStatus>({
    status: "idle",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState<TokenBalance | null>(
    initialToken || null
  );
  const [selectedNetwork, setSelectedNetwork] = useState<string>(
    initialNetwork || ""
  );
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const tokenDropdownRef = useRef<HTMLDivElement>(null);
  // Track previous isOpen to distinguish fresh mounts from actual transitions
  const prevIsOpenRef = useRef<boolean | undefined>(undefined);

  const { address, isExternalEvm } = useParaAccount();
  const { address: wagmiAddress } = useWagmiAccount();
  const chainId = useChainId();
  const queryClient = useQueryClient();
  const { onSendTransaction } = useParaTransactions();
  const { refetch: refreshBalance } = useProfileBalance();
  const para = useClient();
  const { availableTokens, isLoading: isLoadingTokens } = useTokenBalances();
  const { switchChainAsync } = useSwitchChain();
  const wagmiConfig = useConfig();

  // Wagmi hooks for EVM transactions
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();

  // Only reset form when modal transitions from closed to open
  // Using a separate ref to track if we've already initialized for this open session
  const hasInitializedRef = useRef(false);
  const isSwitchingChainRef = useRef(false);
  // Track current txStatus in a ref to avoid stale closure in init useEffect
  const txStatusRef = useRef(txStatus);
  // Track if a transfer is in flight — set synchronously at handleSend start,
  // before any async work or state updates. This guards against race conditions
  // where the component remounts mid-transfer and the txStatusRef sync hasn't run yet.
  const transferInFlightRef = useRef(false);

  // Keep txStatusRef in sync with txStatus state
  useEffect(() => {
    txStatusRef.current = txStatus;
  }, [txStatus]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    // If this is just the initial mount (wasOpen was undefined) with isOpen:true,
    // treat as "already open, don't reset". This prevents resetting mid-transfer
    // when the component remounts but the modal was already in use.
    // Only run reset logic on an actual false→true transition.
    if (wasOpen === undefined && isOpen) {
      logDiagnostic({ event: "transfer-reset-skipped-mount", reason: "initial mount with isOpen:true" });
      hasInitializedRef.current = true;
      return;
    }

    if (isOpen && !hasInitializedRef.current && !isSwitchingChainRef.current) {
      // Don't reset if a transfer is actively in progress (pending or success)
      // This can happen if the modal remounts mid-transfer (e.g. chain switch triggers re-render)
      // Also check transferInFlightRef which is set synchronously before any state updates
      const currentStatus = txStatusRef.current.status;
      if (currentStatus === "pending" || currentStatus === "success" || transferInFlightRef.current) {
        logDiagnostic({
          event: "transfer-reset-to-idle-SKIPPED",
          reason: "transfer in progress",
          currentStatus,
          transferInFlight: transferInFlightRef.current,
        });
        hasInitializedRef.current = true;
        return;
      }
      logDiagnostic({
        event: "transfer-reset-to-idle",
        isOpen,
        hasInitializedRef: hasInitializedRef.current,
        isSwitchingChainRef: isSwitchingChainRef.current,
      });
      setRecipient("");
      setAmount("");
      setTxStatus({ status: "idle" });
      setSelectedToken(initialToken || null);
      setSelectedNetwork(initialNetwork || "");
      setSearchQuery("");
      setShowTokenDropdown(false);
      hasInitializedRef.current = true;
    } else if (!isOpen && !isSwitchingChainRef.current) {
      // Only reset when fully closed to avoid flicker during chain switches
      const timer = setTimeout(() => {
        hasInitializedRef.current = false;
        transferInFlightRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Update selected token if initialToken changes while modal is open (but don't reset form)
  useEffect(() => {
    if (isOpen && initialToken && !selectedToken) {
      setSelectedToken(initialToken);
      setSelectedNetwork(initialNetwork || "");
    }
  }, [isOpen, initialToken, initialNetwork, selectedToken]);

  // Diagnostic: track all txStatus state transitions
  useEffect(() => {
    logDiagnostic({
      event: "transfer-tx-status-changed",
      status: txStatus.status,
      hasHash: !!txStatus.hash,
    });
  }, [txStatus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tokenDropdownRef.current &&
        !tokenDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTokenDropdown(false);
      }
    };

    if (showTokenDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTokenDropdown]);

  const handleSend = async () => {
    // Set transfer in flight FIRST, synchronously, before any state updates or async work.
    // This guards against race conditions where the component remounts mid-transfer.
    transferInFlightRef.current = true;

    if (!selectedToken || !recipient || !amount) {
      transferInFlightRef.current = false;
      return;
    }

    const cleanAmount = amount.trim();
    const amountNum = parseFloat(cleanAmount);
    const balanceNum = parseFloat(selectedToken.balance);

    if (isNaN(amountNum) || amountNum <= 0) {
      setTxStatus({ status: "error", error: "Amount must be greater than 0" });
      return;
    }

    if (amountNum > balanceNum) {
      setTxStatus({ status: "error", error: "Insufficient balance" });
      return;
    }

    let cleanRecipient = recipient.trim();

    if (selectedNetwork === "Solana" || selectedToken?.network === "Solana") {
      if (!cleanRecipient || cleanRecipient.length < 32 || cleanRecipient.length > 44) {
        setTxStatus({ status: "error", error: "Invalid Solana address format" });
        return;
      }
    } else {
      if (!cleanRecipient || !/^0x[a-fA-F0-9]{40}$/.test(cleanRecipient)) {
        setTxStatus({ status: "error", error: "Invalid EVM address format" });
        return;
      }
    }

    setIsLoading(true);
    setTxStatus({ status: "pending" });

    try {
      let txHash: string;
      const useSolanaSendPath =
        selectedNetwork === "Solana" || selectedToken?.network === "Solana";

      console.log("[SendModal] handleSend path selection", {
        path: useSolanaSendPath ? "sendSolanaToken" : "sendEVMToken",
        selectedNetwork,
        tokenNetwork: selectedToken?.network,
        tokenSymbol: selectedToken?.symbol,
        recipient: cleanRecipient,
      });

      if (useSolanaSendPath) {
        txHash = await sendSolanaToken(selectedToken, cleanRecipient, cleanAmount);
      } else {
        txHash = await sendEVMToken(selectedToken, cleanRecipient, cleanAmount);
      }

      logDiagnostic({ event: "transfer-set-status-success", source: "handleSend", hash: txHash.slice(0, 10) });
      setTxStatus({ status: "success", hash: txHash });

      // Store on-chain balance override for EVM transfers to prevent Para data flapping
      // Wait for tx confirmation before reading — sendTransaction resolves on broadcast,
      // not confirmation, so immediate read would get stale pre-transfer balance.
      if (!useSolanaSendPath && selectedToken) {
        const userEvmAddress = address || wagmiAddress;
        const tokenChainId = getChainIdForToken(selectedToken);
        if (userEvmAddress) {
          (async () => {
            try {
              logDiagnostic({ event: "transfer-override-waiting-receipt", hash: txHash.slice(0, 10) });

              const chain = getChainById(tokenChainId);
              // Use the chain's built-in rpcUrls (which point to our DRPC proxy)
              // NOT CHAIN_RPCS which has flaky public endpoints like polygon-rpc.com
              const publicClient = createPublicClient({
                chain,
                transport: http(),
              });

              await publicClient.waitForTransactionReceipt({
                hash: txHash as `0x${string}`,
                timeout: 120_000, // 2min max for slow chains
              });

              logDiagnostic({ event: "transfer-override-receipt-confirmed", hash: txHash.slice(0, 10) });

              await readAndStoreOverride(userEvmAddress, {
                address: selectedToken.address || "",
                chainId: tokenChainId,
                symbol: selectedToken.symbol,
                decimals: selectedToken.decimals ?? 18,
                isNative: isNativeToken(selectedToken.symbol),
              });

              logDiagnostic({ event: "transfer-override-read-done", hash: txHash.slice(0, 10) });

              // Push to transaction history after confirmed success
              pushHistoryEntry({
                type: "transfer",
                hash: txHash,
                status: "success",
                chainId: tokenChainId,
                fromSymbol: selectedToken.symbol,
                fromAmount: amount,
                recipient: recipient,
              });
            } catch (e: any) {
              logDiagnostic({
                event: "transfer-override-error",
                message: e?.message?.slice(0, 200) || "unknown",
              });
            }
          })();
        }
      }

      setTimeout(async () => {
        try {
          await refreshBalance();
        } catch (e) {
          console.warn("Balance refresh failed:", e);
        }
        void queryClient.invalidateQueries();
      }, 3000);

      setTimeout(() => {
        logDiagnostic({ event: "transfer-reset-after-timeout" });
        setRecipient("");
        setAmount("");
        setTxStatus({ status: "idle" });
        transferInFlightRef.current = false;
      }, 3000);
    } catch (error: any) {
      transferInFlightRef.current = false;
      logDiagnostic({
        event: "transfer-error",
        step: "handleSend-catch",
        message: error?.message?.slice(0, 300) || "unknown",
        errorName: error?.name || "unknown",
        errorCause: error?.cause?.message?.slice(0, 200) || null,
        errorShortMessage: error?.shortMessage?.slice(0, 200) || null,
      });
      setTxStatus({
        status: "error",
        error: error.message || "Transaction failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const sendSolanaToken = async (
    token: TokenBalance,
    recipient: string,
    amount: string
  ): Promise<string> => {
    const connection = new Connection(
      typeof window !== "undefined"
        ? `${window.location.origin}/api/rpc?chain=solana`
        : "https://api.mainnet-beta.solana.com",
      { commitment: "confirmed" }
    );
    const fromPublicKey = new PublicKey(address || "");
    const toPublicKey = new PublicKey(recipient);
    const amountLamports = Math.floor(parseFloat(amount) * LAMPORTS_PER_SOL);
    const transaction = new Transaction();

    const currentLamports = await connection.getBalance(fromPublicKey);
    const reserveForFees = Math.floor(SOLANA_GAS_RESERVE * LAMPORTS_PER_SOL);

    if (token.symbol === "SOL") {
      if (amountLamports + reserveForFees > currentLamports) {
        throw new Error(
          `Insufficient SOL balance. Available: ${(currentLamports / LAMPORTS_PER_SOL).toFixed(6)} SOL`
        );
      }
    } else {
      if (currentLamports < reserveForFees) {
        throw new Error("Insufficient SOL for fees");
      }
    }

    if (token.isNative && token.symbol === "SOL") {
      transaction.add(
        SystemProgram.transfer({
          fromPubkey: fromPublicKey,
          toPubkey: toPublicKey,
          lamports: amountLamports,
        })
      );
    } else {
      if (!token.address) {
        throw new Error(`Token address not found for ${token.symbol}`);
      }
      const mintAddress = new PublicKey(token.address);
      let resolvedDecimals = token.decimals;
      let tokenProgramId = TOKEN_PROGRAM_ID;

      if (token.network === "Solana" && token.address && !token.isNative) {
        try {
          const mintInfo = await getMint(
            connection,
            mintAddress,
            "confirmed",
            TOKEN_PROGRAM_ID
          );
          resolvedDecimals = mintInfo.decimals;
          tokenProgramId = TOKEN_PROGRAM_ID;
        } catch {
          try {
            const mintInfo = await getMint(
              connection,
              mintAddress,
              "confirmed",
              TOKEN_2022_PROGRAM_ID
            );
            resolvedDecimals = mintInfo.decimals;
            tokenProgramId = TOKEN_2022_PROGRAM_ID;
          } catch (error) {
            console.warn(
              "Could not fetch on-chain decimals, using fallback:",
              token.decimals,
              error
            );
          }
        }
      }

      const fromTokenAccount = await getAssociatedTokenAddress(
        mintAddress,
        fromPublicKey,
        false,
        tokenProgramId
      );
      const toTokenAccount = await getAssociatedTokenAddress(
        mintAddress,
        toPublicKey,
        false,
        tokenProgramId
      );

      try {
        await getAccount(connection, toTokenAccount, "confirmed", tokenProgramId);
      } catch {
        transaction.add(
          createAssociatedTokenAccountInstruction(
            fromPublicKey,
            toTokenAccount,
            toPublicKey,
            mintAddress,
            tokenProgramId
          )
        );
      }

      console.log("[SendModal] sendSolanaToken resolved decimals", {
        path: "sendSolanaToken",
        tokenSymbol: token.symbol,
        tokenAddress: token.address,
        tokenNetwork: token.network,
        tokenDecimals: token.decimals,
        resolvedDecimals,
        tokenProgramId: tokenProgramId.toBase58(),
        isNative: token.isNative,
      });

      const transferAmount = Math.floor(
        parseFloat(amount) * Math.pow(10, resolvedDecimals)
      );
      transaction.add(
        createTransferInstruction(
          fromTokenAccount,
          toTokenAccount,
          fromPublicKey,
          transferAmount,
          [],
          tokenProgramId
        )
      );
    }

    transaction.feePayer = fromPublicKey;
    return await onSendTransaction(transaction);
  };

  const sendEVMToken = async (
    token: TokenBalance,
    recipient: string,
    amount: string
  ): Promise<string> => {
    const tokenChainId = getChainIdForToken(token);
    logDiagnostic({
      event: "transfer-initiated",
      tokenSymbol: token.symbol,
      chainId: tokenChainId,
      isNative: isNativeToken(token.symbol),
      isExternalEvm,
    });
    console.log("🔍 sendEVMToken called:", {
      tokenSymbol: token.symbol,
      tokenChainId,
      tokenAddress: token.address,
      isExternalEvm,
      recipient,
      amount,
    });

    if (!address) {
      throw new Error("Wallet not connected");
    }

    const currentChain = getChainById(tokenChainId);

    if (!currentChain) {
      throw new Error(`Unsupported chain ID: ${tokenChainId}`);
    }

    const completeWithSuccess = (hash: string) => {
      logDiagnostic({ event: "transfer-set-status-success", source: "completeWithSuccess", hash: hash.slice(0, 10) });
      setTxStatus({ status: "success", hash });
      return hash;
    };

    // Check if this is a native token (ETH, BNB, MATIC, etc.)
    // token.isNative is reliably set by both Para and wagmi data sources.
    // Do NOT gate on !token.address — wagmi returns zeroAddress for natives which evaluates truthy.
    const nativeSymbols = ["ETH", "BNB", "MATIC", "POL", "AVAX"];
    const isNativeTransfer = token.isNative ||
      (!token.address && nativeSymbols.includes(token.symbol.toUpperCase()));

    logDiagnostic({
      event: "transfer-native-check",
      tokenSymbol: token.symbol,
      tokenAddress: token.address,
      tokenIsNative: !!token.isNative,
      isNativeTransfer,
      chainId: tokenChainId,
    });

    // Para-managed wallet path: uses wagmi pattern that goes through Para's connector system.
    // This handles wallet type switching (Solana→EVM) internally, avoiding the
    // "no valid wallet id" error from createParaViemClient when Para SDK is in Solana mode.
    // Same pattern as lifi-sdk-solana-provider.tsx.
    if (!isExternalEvm) {
      setTxStatus({ status: "pending", message: `Switching to ${currentChain.name}...` });

      // Hydrate Para wallet cache before transfer (same pattern as swap flow)
      // init() is idempotent and ensures getWalletsByType('EVM') returns wallets
      const PARA_INIT_TIMEOUT_MS = 60_000;
      if (para) {
        logDiagnostic({ event: "transfer-para-init-start" });
        await Promise.race([
          para.init(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error("Para signing service is slow. Please try again.")),
              PARA_INIT_TIMEOUT_MS
            )
          ),
        ]);
        logDiagnostic({ event: "transfer-para-init-done" });
      }

      try {
        isSwitchingChainRef.current = true;
        logDiagnostic({ event: "transfer-switch-chain-start", chainId: tokenChainId });
        await switchChainAction(wagmiConfig, { chainId: tokenChainId });
        logDiagnostic({ event: "transfer-switch-chain-done" });
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error: any) {
        logDiagnostic({
          event: "transfer-error",
          step: "switch-chain",
          message: error?.message?.slice(0, 300) || "unknown",
          errorName: error?.name || "unknown",
          errorCause: error?.cause?.message?.slice(0, 200) || null,
          errorShortMessage: error?.shortMessage?.slice(0, 200) || null,
        });
        throw new Error(`Failed to switch to ${currentChain.name}: ${error.message}`);
      } finally {
        isSwitchingChainRef.current = false;
      }

      logDiagnostic({ event: "transfer-get-wallet-client-start" });
      const walletClient = await getWalletClientAction(wagmiConfig, { chainId: tokenChainId });
      logDiagnostic({
        event: "transfer-get-wallet-client-done",
        hasClient: !!walletClient,
        hasAccount: !!walletClient?.account,
      });
      if (!walletClient?.account) {
        throw new Error("Failed to get wallet client. Please reconnect your wallet.");
      }

      setTxStatus({ status: "pending", message: "Confirm transaction in your wallet..." });

      if (isNativeTransfer) {
        const amountNum = parseFloat(amount);
        const balanceNum = parseFloat(token.balance);
        const gasReserve = await estimateEvmGasCost(tokenChainId);
        if (amountNum + gasReserve > balanceNum) {
          throw new Error(
            `Insufficient balance for gas. Max sendable: ${(balanceNum - gasReserve).toFixed(6)} ${token.symbol}`
          );
        }
        let gasParams = {};
        if (tokenChainId === 1) {
          logDiagnostic({ event: "transfer-gas-params-start", chainId: tokenChainId });
          gasParams = await getEthereumMainnetGasParams();
          logDiagnostic({
            event: "transfer-gas-params-done",
            hasMaxFeePerGas: !!(gasParams as any)?.maxFeePerGas,
            hasMaxPriorityFeePerGas: !!(gasParams as any)?.maxPriorityFeePerGas,
          });
        }
        logDiagnostic({ event: "transfer-send-tx-start", isErc20: false });
        const hash = await walletClient.sendTransaction({
          to: recipient as `0x${string}`,
          value: parseUnits(safeFormatAmount(amount), 18),
          account: walletClient.account,
          chain: currentChain,
          ...gasParams,
        });
        logDiagnostic({ event: "transfer-send-tx-done", hasHash: !!hash });
        return completeWithSuccess(hash);
      }

      if (!token.address) {
        throw new Error(
          `Token address not found for ${token.symbol}. This may be a native token - please check your balance.`
        );
      }
      // Truncate amount to token decimals to prevent "exceeds balance" errors
      const safeAmount = safeFormatAmount(amount);
      const transferAmount = parseErc20TransferAmount({
        amount: safeAmount,
        decimals: token.decimals,
        tokenSymbol: token.symbol,
      });
      assertValidErc20TransferParams({
        tokenAddress: token.address,
        recipient,
        amount: transferAmount,
        tokenSymbol: token.symbol,
      });
      const data = encodeFunctionData({
        abi: ERC20_ABI,
        functionName: "transfer",
        args: [recipient as `0x${string}`, transferAmount],
      });
      let gasParams = {};
      if (tokenChainId === 1) {
        logDiagnostic({ event: "transfer-gas-params-start", chainId: tokenChainId });
        gasParams = await getEthereumMainnetGasParams();
        logDiagnostic({
          event: "transfer-gas-params-done",
          hasMaxFeePerGas: !!(gasParams as any)?.maxFeePerGas,
          hasMaxPriorityFeePerGas: !!(gasParams as any)?.maxPriorityFeePerGas,
        });
      }
      logDiagnostic({ event: "transfer-send-tx-start", isErc20: true });
      const hash = await walletClient.sendTransaction({
        to: token.address as `0x${string}`,
        data,
        account: walletClient.account,
        chain: currentChain,
        ...gasParams,
      });
      logDiagnostic({ event: "transfer-send-tx-done", hasHash: !!hash });
      return completeWithSuccess(hash);
    }

    // External wallet path: relies on wagmi hooks directly (switchChain/send/write).
    // No wagmi config is required in this flow either.
    setTxStatus({ status: "pending", message: `Switching to ${currentChain.name}...` });
    try {
      isSwitchingChainRef.current = true;
      await switchChainAsync({ chainId: tokenChainId });
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (error: any) {
      throw new Error(`Failed to switch to ${currentChain.name}: ${error.message}`);
    } finally {
      isSwitchingChainRef.current = false;
    }

    setTxStatus({ status: "pending", message: "Confirm transaction in your wallet..." });

    if (isNativeTransfer) {
      const gasParams = tokenChainId === 1 ? await getEthereumMainnetGasParams() : {};
      const hash = await sendTransactionAsync({
        to: recipient as `0x${string}`,
        value: parseUnits(safeFormatAmount(amount), 18),
        chainId: tokenChainId,
        ...gasParams,
      });
      return completeWithSuccess(hash);
    }

    if (!token.address) {
      throw new Error(
        `Token address not found for ${token.symbol}. This may be a native token - please check your balance.`
      );
    }
    const gasParams = tokenChainId === 1 ? await getEthereumMainnetGasParams() : {};
    const hash = await writeContractAsync({
      address: token.address as `0x${string}`,
      abi: ERC20_ABI,
      functionName: "transfer",
      args: [
        recipient as `0x${string}`,
        parseUnits(safeFormatAmount(amount), token.decimals),
      ],
      chainId: tokenChainId,
      ...gasParams,
    });
    return completeWithSuccess(hash);
  };

  const getExplorerUrl = (hash: string) => {
    if (selectedNetwork === "Solana" || selectedToken?.network === "Solana") {
      return getSolanaExplorerUrl(hash);
    }
    const tokenChain = selectedToken ? getChainIdForToken(selectedToken) : chainId;
    return getExplorerUrlByChain(tokenChain, hash);
  };

  const getNetworkBadgeColor = (network: string) => {
    const colors: Record<string, string> = {
      Ethereum: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      Base: "bg-blue-400/20 text-blue-300 border-blue-400/30",
      Polygon: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      Arbitrum: "bg-blue-600/20 text-blue-400 border-blue-600/30",
      Optimism: "bg-[#f9cd4f]/20 text-[#f9cd4f] border-[#f9cd4f]/30",
      Solana: "bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border-purple-500/30",
      BNB: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      Bsc: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    };
    return colors[network] || "bg-muted/20 text-muted-foreground border-muted/30";
  };

  const filteredTokens = availableTokens?.filter(
    (token) =>
      token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      token.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px] max-h-[90vh] bg-card border-border p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="p-6 pb-4 border-b border-border flex-shrink-0">
          <DialogTitle className="text-foreground text-xl font-semibold flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <Send className="h-5 w-5 text-primary" />
            </div>
            Send {selectedToken?.symbol || "Tokens"}
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Token Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">Select Token</label>
            <div className="relative" ref={tokenDropdownRef}>
              <button
                type="button"
                onClick={() => setShowTokenDropdown(!showTokenDropdown)}
                className="w-full flex items-center justify-between p-4 rounded-xl bg-input border border-border hover:border-primary/50 transition-colors"
              >
                {selectedToken ? (
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 rounded-xl">
                      {selectedToken.logoURI && (
                        <AvatarImage src={selectedToken.logoURI} alt={selectedToken.symbol} className="rounded-xl object-cover" />
                      )}
                      <AvatarFallback className="bg-primary/20 text-primary rounded-xl font-bold">
                        {selectedToken.symbol.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground font-medium">{selectedToken.symbol}</span>
                        {selectedToken.network && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getNetworkBadgeColor(selectedToken.network)}`}>
                            {selectedToken.network}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {parseFloat(selectedToken.balance).toFixed(4)} available
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-muted/50 flex items-center justify-center">
                      <Wallet className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <span className="text-muted-foreground">Select a token</span>
                  </div>
                )}
                <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${showTokenDropdown ? "rotate-180" : ""}`} />
              </button>

              {showTokenDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-3 border-b border-border">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search tokens..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-input border-border"
                      />
                    </div>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {isLoadingTokens ? (
                      <div className="p-8 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                        <p className="text-sm text-muted-foreground">Loading tokens...</p>
                      </div>
                    ) : filteredTokens.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground">No tokens found</div>
                    ) : (
                      filteredTokens.map((token) => (
                        <button
                          key={`${token.symbol}-${token.network}-${token.address || "native"}`}
                          onClick={() => {
                            setSelectedToken(token);
                            setSelectedNetwork(token.network || "");
                            setShowTokenDropdown(false);
                            setSearchQuery("");
                          }}
                          className="w-full p-3 hover:bg-muted/30 flex items-center justify-between transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9 rounded-lg">
                              {token.logoURI && (
                                <AvatarImage src={token.logoURI} alt={token.symbol} className="rounded-lg object-cover" />
                              )}
                              <AvatarFallback className="bg-primary/20 text-primary rounded-lg text-sm font-bold">
                                {token.symbol.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <span className="text-foreground font-medium text-sm">{token.symbol}</span>
                                {token.network && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${getNetworkBadgeColor(token.network)}`}>
                                    {token.network}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{token.name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-foreground text-sm font-medium">
                              {parseFloat(token.balance).toFixed(4)}
                            </p>
                            <p className="text-xs text-muted-foreground">${token.balanceUSD.toFixed(2)}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recipient Address */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">
              Recipient Address
            </label>
            <Input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder={
                selectedToken?.network === "Solana"
                  ? "Enter Solana address..."
                  : "Enter wallet address (0x...)"
              }
              className="bg-input border-border h-12"
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-muted-foreground">Amount</label>
              {selectedToken && (
                <button
                  type="button"
                  onClick={async () => {
                    const balance = parseFloat(selectedToken.balance);
                    const isSolana = selectedToken.network === "Solana";
                    const nativeSymbols = ["ETH", "BNB", "MATIC", "POL", "AVAX"];
                    // Match the same logic as isNativeTransfer in sendEVMToken validator
                    const isNativeEvm = selectedToken.isNative ||
                      (!selectedToken.address && nativeSymbols.includes(selectedToken.symbol.toUpperCase()));
                    const isNativeSol = isSolana && selectedToken.symbol === "SOL";

                    if (isNativeEvm) {
                      // Estimate gas dynamically from the network
                      const tokenChainId = getChainIdForToken(selectedToken);
                      const gasReserve = await estimateEvmGasCost(tokenChainId);
                      if (balance > gasReserve) {
                        setAmount(safeFormatAmount((balance - gasReserve).toString()));
                      } else {
                        toast.info("Insufficient balance for gas fees");
                        setAmount("0");
                      }
                    } else if (isNativeSol) {
                      // Reserve fees for native SOL
                      const maxAmount = Math.max(0, balance - SOLANA_GAS_RESERVE);
                      if (maxAmount > 0) {
                        setAmount(safeFormatAmount(maxAmount.toString()));
                      } else {
                        toast.info("Insufficient balance for transaction fees");
                        setAmount("0");
                      }
                    } else {
                      // For token sends, cap to 4 decimal places to avoid precision overflow
                      if (!isSolana && selectedToken.address) {
                        setAmount(safeFormatAmount(selectedToken.balance));
                      } else {
                        // For SPL tokens, use full balance (fees paid separately)
                        setAmount(safeFormatAmount(selectedToken.balance));
                      }
                    }
                  }}
                  className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  MAX
                </button>
              )}
            </div>
            <div className="relative">
              <Input
                type="text"
                value={amount}
                onChange={(e) => {
                  if (/^\d*\.?\d*$/.test(e.target.value)) {
                    setAmount(e.target.value);
                  }
                }}
                placeholder="0.00"
                className="bg-input border-border h-12 pr-20 text-lg"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">
                {selectedToken?.symbol || "TOKEN"}
              </div>
            </div>
            {amount && selectedToken && (
              <p className="text-sm text-muted-foreground">
                ≈ ${(parseFloat(amount || "0") * selectedToken.price).toFixed(2)} USD
              </p>
            )}
          </div>

          {/* Transaction Status */}
          {txStatus.status !== "idle" && (
            <Card className={`border overflow-hidden ${
              txStatus.status === "success"
                ? "border-green-500/30 bg-green-500/10"
                : txStatus.status === "error"
                  ? "border-destructive/30 bg-destructive/10"
                  : "border-primary/30 bg-primary/10"
            }`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {txStatus.status === "pending" && (
                    <Loader2 className="h-5 w-5 text-primary animate-spin flex-shrink-0 mt-0.5" />
                  )}
                  {txStatus.status === "success" && (
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  )}
                  {txStatus.status === "error" && (
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  )}

                  <div className="flex-1 min-w-0 overflow-hidden">
                    {txStatus.status === "pending" && (
                      <p className="text-primary font-medium text-sm">
                        {txStatus.message || "Processing transaction..."}
                      </p>
                    )}
                    {txStatus.status === "success" && (
                      <div>
                        <p className="text-green-500 font-medium text-sm">Transaction Successful!</p>
                        {txStatus.hash && (
                          <a
                            href={getExplorerUrl(txStatus.hash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                          >
                            View on Explorer
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    )}
                    {txStatus.status === "error" && (
                      <p className="text-destructive font-medium text-sm break-words line-clamp-2">
                        {txStatus.error || "Transaction Failed"}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </div>

        {/* Action Buttons - Fixed Footer */}
        <div className="flex gap-3 p-6 pt-4 border-t border-border flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 h-12 border-border hover:bg-muted/50 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!selectedToken || !recipient || !amount || isLoading || txStatus.status === "pending"}
            className="flex-1 h-12 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
